import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useAccountId } from '@labrav/common'
import { ExternalLink, NoteV2 } from '@labrav/react-components'
import { Typography } from '@mui/material'
import { Link } from 'react-router-dom'
import { getFlyoutNewOnboardingFormData } from '../../api/marketplace'
import { msftSecretTokenLink } from '../../common/utils/constants'
import { CloudType } from '../../types'
import { useStyles } from './ClientSecretExpirationBanner.styles'
import {
  getClientSecretExpirationStatus,
  type ClientSecretExpirationStatus,
} from './getClientSecretExpirationStatus'

export type { ClientSecretExpirationStatus }
export { getClientSecretExpirationStatus }

const toIsoDateTimeString = (value: string): string => {
  const trimmed = value.trim()
  if (!trimmed) return ''

  const dateMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/)
  if (!dateMatch) return trimmed

  return `${dateMatch[1]}T00:00:00.000Z`
}

const getClientSecretExpirationDateFromOnboarding = (data: unknown): string => {
  const apiResponse = data as {
    draft_json?: {
      cloud_settings?: {
        cloud_credentials?: {
          client_secret_expiration_date?: string
        }
      }
    }
  }

  return toIsoDateTimeString(
    apiResponse?.draft_json?.cloud_settings?.cloud_credentials
      ?.client_secret_expiration_date || ''
  )
}

const fetchClientSecretExpirationDate = async (
  accountId: string
): Promise<string> => {
  const { data } = await getFlyoutNewOnboardingFormData(
    accountId,
    CloudType.AZURE
  )

  return getClientSecretExpirationDateFromOnboarding(data)
}

export type ClientSecretExpirationBannerProps = {
  expirationDate?: string
  accountId?: string
  variant?: 'note' | 'banner'
  fetchOnboardingIfMissing?: boolean
  showNotConfiguredMessage?: boolean
  hideSettingsPath?: boolean
  onStatusChange?: (status: ClientSecretExpirationStatus | null) => void
}

type MessageClasses = ReturnType<typeof useStyles>['classes']

const ClientSecretExpirationMessage = ({
  status,
  settingsPath,
  hideSettingsPath,
  classes,
}: {
  status: ClientSecretExpirationStatus
  settingsPath: string
  hideSettingsPath?: boolean
  classes: MessageClasses
}) => {
  if (status.type === 'notConfigured') {
    return (
      <>
        Client secret expiration date is{' '}
        <span className={classes.highlightText}>not configured in Labra</span>.
        {hideSettingsPath ? (
          ' Add the expiration date below.'
        ) : (
          <>
            {' '}
            Go to{' '}
            <Link
              to={settingsPath}
              className={classes.settingsLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              Settings
            </Link>{' '}
            to add the expiration date.
          </>
        )}
      </>
    )
  }

  return (
    <>
      {status.type === 'expired' ? (
        <>
          Secret token has{' '}
          <span className={classes.highlightText}>expired</span>.
        </>
      ) : (
        <>
          Secret token{' '}
          <span className={classes.highlightText}>
            expires in {status.daysRemaining} days
          </span>
          .
        </>
      )}{' '}
      Refer to <ExternalLink href={msftSecretTokenLink}>this Doc</ExternalLink>,
      to create a new client secret token
      {hideSettingsPath ? (
        ' and update the expiration date below.'
      ) : (
        <>
          {', then go to '}
          <Link
            to={settingsPath}
            className={classes.settingsLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            Settings
          </Link>
          {' to update the expiration date.'}
        </>
      )}
    </>
  )
}

export const ClientSecretExpirationBanner: React.FC<
  ClientSecretExpirationBannerProps
> = ({
  expirationDate: expirationDateFromProps,
  accountId: accountIdFromProps,
  variant = 'banner',
  fetchOnboardingIfMissing = true,
  showNotConfiguredMessage = true,
  hideSettingsPath = false,
  onStatusChange,
}) => {
  const fallbackAccountId = useAccountId({
    product: 'flyout',
    cloud: CloudType.AZURE,
  })
  const accountId = accountIdFromProps ?? fallbackAccountId
  const [fetchedOnboarding, setFetchedOnboarding] = useState<{
    accountId: string
    expirationDate: string
  } | null>(null)

  const usesOnboardingFetch =
    fetchOnboardingIfMissing && expirationDateFromProps === undefined

  const isFetchComplete =
    !usesOnboardingFetch || fetchedOnboarding?.accountId === accountId

  const clientSecretExpirationDate =
    expirationDateFromProps ??
    (isFetchComplete ? fetchedOnboarding?.expirationDate : undefined)

  useEffect(() => {
    if (!usesOnboardingFetch || !accountId) {
      return
    }

    let isStale = false
    setFetchedOnboarding(null)

    void fetchClientSecretExpirationDate(accountId)
      .then(expirationDate => {
        if (!isStale) {
          setFetchedOnboarding({ accountId, expirationDate })
        }
      })
      .catch(() => {
        if (!isStale) {
          setFetchedOnboarding({ accountId, expirationDate: '' })
        }
      })

    return () => {
      isStale = true
    }
  }, [accountId, usesOnboardingFetch])

  const settingsPath = useMemo(() => {
    if (!accountId) {
      return '/settings/flyout/cloud/azure'
    }
    return `/settings/flyout/cloud/azure/${accountId}`
  }, [accountId])

  const clientSecretExpirationStatus = useMemo(
    () =>
      getClientSecretExpirationStatus(
        clientSecretExpirationDate,
        showNotConfiguredMessage
      ),
    [clientSecretExpirationDate, showNotConfiguredMessage]
  )

  const onStatusChangeRef = useRef(onStatusChange)
  onStatusChangeRef.current = onStatusChange

  useEffect(() => {
    if (!onStatusChangeRef.current) {
      return
    }
    if (!isFetchComplete) {
      onStatusChangeRef.current(null)
      return
    }
    onStatusChangeRef.current(clientSecretExpirationStatus)
  }, [isFetchComplete, clientSecretExpirationStatus])

  useEffect(
    () => () => {
      onStatusChangeRef.current?.(null)
    },
    []
  )

  const { classes } = useStyles({
    statusType: clientSecretExpirationStatus?.type ?? 'expiring',
  })

  if (!isFetchComplete || !clientSecretExpirationStatus) {
    return null
  }

  const message = (
    <ClientSecretExpirationMessage
      status={clientSecretExpirationStatus}
      settingsPath={settingsPath}
      hideSettingsPath={hideSettingsPath}
      classes={classes}
    />
  )

  if (variant === 'note') {
    return (
      <NoteV2
        description={
          <Typography
            component="span"
            data-testid="client-secret-expiration-note"
          >
            {message}
          </Typography>
        }
      />
    )
  }

  return (
    <div
      className={classes.bannerRoot}
      data-testid="client-secret-expiration-banner"
    >
      <div className={classes.bannerContent}>{message}</div>
    </div>
  )
}
